import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersService, AdminUserRecord, UserFilters } from '@/services/users';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { TableSkeleton } from '@/components/skeletons';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Search, RefreshCw, AlertCircle, Users, Filter, Trash2, Pencil, Key, Loader2, Link2, Copy
} from 'lucide-react';
import { UserEditModal } from './components/UserEditModal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { generatePaginationPages } from '@/lib/pagination';
import { useIsViewer, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer';
import { RelativeTime } from '@/components/RelativeTime';
import { useDebounce } from '@/hooks/useDebounce';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import toastService from '@/services/toast/service';


const COLUMN_COUNT = 5;

// User accounts that must never be deleted from the backoffice
const PROTECTED_USER_EMAILS = new Set(['ceo@passive-automation.com', 'joe@doe.com']);

const isProtectedUser = (user: AdminUserRecord) =>
  PROTECTED_USER_EMAILS.has(user.email.toLowerCase());

export default function UsersList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState<number | null>(null);

  // Filter state
  const [filters, setFilters] = useState<UserFilters>({});

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUserRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const isViewer = useIsViewer();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [userToReset, setUserToReset] = useState<AdminUserRecord | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const [magicLinkModalOpen, setMagicLinkModalOpen] = useState(false);
  const [magicLinkUser, setMagicLinkUser] = useState<AdminUserRecord | null>(null);
  const [magicLinkUrl, setMagicLinkUrl] = useState<string | null>(null);
  const [generatingLinkUserId, setGeneratingLinkUserId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const hasActiveFilters = !!(filters.role);

  const selectableUsers = users.filter(u => !isProtectedUser(u));

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await usersService.getUsers(page, 20, debouncedSearch, 'createdAtUtc', 'desc', filters);
      setUsers(res.data);
      setTotalPages(res.meta.totalPages);
      setTotalUsers(res.meta.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, filters]);

  // A search or filter change goes back to page 1 before it fetches, so it
  // costs one request rather than one for the old page and one for the first.
  const filterKey = JSON.stringify([debouncedSearch, filters]);
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current !== filterKey) {
      lastFilterKey.current = filterKey;
      if (page !== 1) {
        setPage(1);
        return;
      }
    }
    fetchUsers();
  }, [fetchUsers, filterKey, page]);

  // Clear selection whenever the visible rows change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, debouncedSearch, filters]);

  const handleDeleteClick = (user: AdminUserRecord) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleEditClick = (user: AdminUserRecord) => {
    setEditingUserId(user.id);
    setEditModalOpen(true);
  };

  const handleResetPasswordClick = (user: AdminUserRecord) => {
    setUserToReset(user);
    setResetPasswordModalOpen(true);
  };

  const handleGenerateLinkClick = async (user: AdminUserRecord) => {
    setGeneratingLinkUserId(user.id);
    try {
      const { url } = await usersService.generateMagicLink(user.id);
      setMagicLinkUser(user);
      setMagicLinkUrl(url);
      setMagicLinkModalOpen(true);
    } catch (err: any) {
      toastService.error(err.response?.data?.message || "Couldn't generate login link");
    } finally {
      setGeneratingLinkUserId(null);
    }
  };

  const handleCopyMagicLink = async () => {
    if (!magicLinkUrl) return;
    try {
      await navigator.clipboard.writeText(magicLinkUrl);
      toastService.success('Link copied');
    } catch {
      toastService.error("Couldn't copy link");
    }
  };

  const confirmResetPassword = async () => {
    if (!userToReset) return;
    setIsResetting(true);
    try {
      await usersService.resetUserPassword(userToReset.id);
      toastService.success(`Password reset email sent to ${userToReset.email}`);
    } catch (err: any) {
      toastService.error(err.response?.data?.message || "Couldn't send reset email");
    } finally {
      setIsResetting(false);
      setResetPasswordModalOpen(false);
      setUserToReset(null);
    }
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await usersService.deleteUser(userToDelete.id);
      toastService.success(`"${userToDelete.firstName} ${userToDelete.lastName}" deleted`);
      fetchUsers();
    } catch (err: any) {
      toastService.error(err.response?.data?.message || "Couldn't delete user");
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const allSelected = selectableUsers.length > 0 && selectableUsers.every(u => prev.has(u.id));
      return allSelected ? new Set() : new Set(selectableUsers.map(u => u.id));
    });
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setIsBulkDeleting(true);
    let succeeded = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await usersService.deleteUser(id);
        succeeded++;
      } catch {
        failed++;
      }
    }
    if (succeeded > 0) toastService.success(`${succeeded} user${succeeded === 1 ? '' : 's'} deleted`);
    if (failed > 0) toastService.error(`Couldn't delete ${failed} user${failed === 1 ? '' : 's'}`);
    setIsBulkDeleting(false);
    setBulkDeleteModalOpen(false);
    setSelectedIds(new Set());
    fetchUsers();
  };

  return (
    <div className="space-y-4 sm:space-y-6 w-full px-4 sm:px-0">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">
          Users{' '}
          {totalUsers !== null && (
            <span className="text-muted-foreground font-normal">
              ({totalUsers})
            </span>
          )}
        </h1>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {selectedIds.size > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="w-full sm:w-auto">
                  <Button
                    variant="destructive"
                    onClick={() => setBulkDeleteModalOpen(true)}
                    disabled={isLoading || isBulkDeleting || isViewer}
                    className="w-full sm:w-auto"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete ({selectedIds.size})
                  </Button>
                </span>
              </TooltipTrigger>
              {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
            </Tooltip>
          )}
          <Button
            variant="outline"
            onClick={() => fetchUsers()}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : (
        <div className="rounded-lg border bg-card">
          {/* Toolbar — search left, filters right */}
          <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between border-b">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9${isLoading ? ' opacity-50 cursor-not-allowed' : ''}`}
                readOnly={isLoading}
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Select
                value={filters.role || 'all'}
                onValueChange={(v) => setFilters(prev => ({ ...prev, role: v === 'all' ? undefined : v }))}
              >
                <SelectTrigger className="h-9 w-[150px] text-sm">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[40px] text-center">
                    <Checkbox
                      checked={selectableUsers.length > 0 && selectableUsers.every(u => selectedIds.has(u.id))}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all users on this page"
                    />
                  </TableHead>
                  <TableHead className="min-w-[200px]">User</TableHead>
                  <TableHead className="text-center min-w-[200px]">Agencies</TableHead>
                  <TableHead className="text-center w-[150px]">Created At</TableHead>
                  <TableHead className="text-center w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className={ isLoading ? 'pointer-events-none' : '' }>
              {isLoading ? (
                <TableSkeleton columns={COLUMN_COUNT} rows={5} />
              ) : users.length === 0 && debouncedSearch ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Search className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">No results found</h3>
                      <p className="text-sm text-muted-foreground">
                        No users match your search. Try a different query.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 && hasActiveFilters ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Filter className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">No matching users</h3>
                      <p className="text-sm text-muted-foreground">
                        No users match your current filters.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-48 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                      <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                        <Users className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">No users found</h3>
                      <p className="text-sm text-muted-foreground">
                        There are no users in the system yet.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()} className="text-center">
                      <Checkbox
                        checked={selectedIds.has(user.id)}
                        onCheckedChange={() => toggleSelectOne(user.id)}
                        disabled={isProtectedUser(user)}
                        aria-label={`Select ${user.firstName} ${user.lastName}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-sm">{user.firstName} {user.lastName}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    </TableCell>

                    <TableCell className="text-center">
                      {user.agencies.length > 0 ? (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {user.agencies.slice(0, 2).map((a) => (
                            <Badge key={a.id} variant="secondary" className="font-medium text-xs shadow-none cursor-default">
                              {a.name}
                            </Badge>
                          ))}
                          {user.agencies.length > 2 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="secondary" className="font-medium text-xs shadow-none cursor-help bg-muted/50">
                                  +{user.agencies.length - 2} more
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="flex flex-col gap-1 text-xs text-left">
                                  {user.agencies.slice(2).map(a => (
                                    <div key={a.id}>{a.name}</div>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm italic">No Agency</span>
                      )}
                    </TableCell>



                    <TableCell className="text-center text-muted-foreground">
                      <RelativeTime date={user.createdAtUtc} className="cursor-default" />
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center">
                        <div className="inline-flex items-center rounded-md border">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={isProtectedUser(user) || isViewer}
                                  className="h-8 w-8 rounded-none rounded-l-md border-r hover:bg-destructive/10 hover:text-destructive"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteClick(user); }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : isProtectedUser(user) ? 'This user cannot be deleted' : 'Delete User'}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={isViewer}
                                  className="h-8 w-8 rounded-none border-r"
                                  onClick={(e) => { e.stopPropagation(); handleResetPasswordClick(user); }}
                                >
                                  <Key className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : 'Reset Password'}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={isViewer || generatingLinkUserId === user.id}
                                  className="h-8 w-8 rounded-none border-r"
                                  onClick={(e) => { e.stopPropagation(); handleGenerateLinkClick(user); }}
                                >
                                  {generatingLinkUserId === user.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Link2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : 'Generate Login Link'}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={isViewer}
                                  className="h-8 w-8 rounded-none rounded-r-md"
                                  onClick={(e) => { e.stopPropagation(); handleEditClick(user); }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{isViewer ? VIEWER_NO_PERMISSION : 'Edit User'}</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
              </TableBody>
            </Table>
          </div>
          
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {users.length} of {totalUsers !== null ? totalUsers : '...'} users
              </p>
              <Pagination className={`mx-0 w-auto ${isLoading ? 'pointer-events-none opacity-50' : ''}`}>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  {generatePaginationPages(page, totalPages).map((item, i) =>
                    item === 'ellipsis' ? (
                      <PaginationItem key={`ellipsis-${i}`}>
                        <span className="px-2 text-muted-foreground">…</span>
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={item}>
                        <PaginationLink
                          onClick={() => setPage(item as number)}
                          isActive={(item as number) === page}
                          className="cursor-pointer"
                        >
                          {item}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Are you sure you want to delete user <strong className="break-all">{userToDelete?.firstName} {userToDelete?.lastName}</strong> ({userToDelete?.email})?
              </p>
              <div className="bg-destructive/10 border-l-4 border-destructive p-3 text-destructive rounded-r text-sm">
                <strong>Warning:</strong> This action is irreversible. If this user is the only Super Admin for any agencies, those agencies and all their data (forms, leads, etc.) will also be permanently deleted.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete User'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Modal */}
      <AlertDialog open={bulkDeleteModalOpen} onOpenChange={setBulkDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} user{selectedIds.size === 1 ? '' : 's'}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Are you sure you want to delete {selectedIds.size} selected user{selectedIds.size === 1 ? '' : 's'}?
              </p>
              <div className="bg-destructive/10 border-l-4 border-destructive p-3 text-destructive rounded-r text-sm">
                <strong>Warning:</strong> This action is irreversible. If any user is the only Super Admin for an agency, that agency and all its data (forms, leads, etc.) will also be permanently deleted.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={confirmBulkDelete}
              disabled={isBulkDeleting}
            >
              {isBulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size} user${selectedIds.size === 1 ? '' : 's'}`}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Confirmation Modal */}
      <AlertDialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to send a password reset email to <strong>{userToReset?.email}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
            <Button
              onClick={confirmResetPassword}
              disabled={isResetting}
            >
              {isResetting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Email'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Login Link Modal */}
      <AlertDialog
        open={magicLinkModalOpen}
        onOpenChange={(open) => {
          setMagicLinkModalOpen(open);
          if (!open) {
            setMagicLinkUser(null);
            setMagicLinkUrl(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Login Link</AlertDialogTitle>
            <AlertDialogDescription>
              One-time login link for <strong className="break-all">{magicLinkUser?.email}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogBody className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={magicLinkUrl ?? ''}
                className="text-xs"
                onFocus={(e) => e.target.select()}
              />
              <Button variant="outline" size="icon" className="shrink-0" onClick={handleCopyMagicLink}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              This link expires in 24 hours and can be used once.
            </p>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Modal */}
      {editingUserId && (
        <UserEditModal
          userId={editingUserId}
          open={editModalOpen}
          onOpenChange={(open) => {
            setEditModalOpen(open);
            if (!open) setEditingUserId(null);
          }}
          onSuccess={fetchUsers}
        />
      )}
    </div>
  );
}
